import React from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../../components/Input";
import TextArea from "../../../../../components/TextArea";
import Checkbox from "../../../../../components/Checkbox";
import Link from "../../../../../components/Link";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { formatAmount, formatDollarAmount } from "../../../../../utils/methods";
import { AdjustCommissionForm, CommissionPayoutInvoice } from "../../../../../interfaces/managerStatement.interface";

interface AdjustCommissionModalContentProps {
  item: CommissionPayoutInvoice | null;
  formData: AdjustCommissionForm;
  isAdminCaller: boolean;
  readOnly?: boolean;
  onInputChange: (e: any, name: string) => void;
  onResetToAuto: (field: 'base_commission_override' | 'property_visit_commission_override') => void;
}

const AdjustCommissionModalContent: React.FC<AdjustCommissionModalContentProps> = ({
  item, formData, isAdminCaller, readOnly, onInputChange, onResetToAuto,
}) => {
  const { t } = useTranslation();
  const base = 'reconciliation.managerStatement.detail.pageTexts.detailsTab.commissionPayouts.adjustModal';

  if (!item) return null;

  return (
    <div className="py-2">
      <div className="bg-body rounded p-3 mb-4">
        <div className="row g-3">
          <div className="col-6">
            <div className="text-heading text-uppercase small">{t(`${base}.summary.coHostCommission`)}</div>
            <div className="fw-semibold">{formatDollarAmount(formatAmount(Number(item.co_host_commission || 0).toFixed(2)))}</div>
          </div>
          <div className="col-6">
            <div className="text-heading text-uppercase small">{t(`${base}.summary.autoBaseCommission`)}</div>
            <div className="fw-semibold">{formatDollarAmount(formatAmount(Number(item.base_commission_auto || 0).toFixed(2)))}</div>
          </div>
          <div className="col-6">
            <div className="text-heading text-uppercase small">{t(`${base}.summary.autoPropertyVisitCommission`)}</div>
            <div className="fw-semibold">{formatDollarAmount(formatAmount(Number(item.property_visit_commission_auto || 0).toFixed(2)))}</div>
          </div>
          <div className="col-6">
            <div className="text-heading text-uppercase small">{t(`${base}.summary.autoCommissionEarned`)}</div>
            <div className="fw-semibold text-success">
              {formatDollarAmount(formatAmount(Number((item.base_commission_auto || 0) + (item.property_visit_commission_auto || 0)).toFixed(2)))}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {isAdminCaller && (
          <div className="col-12 d-flex align-items-center gap-2">
            <Checkbox
              variant="primary"
              className="d-checkbox mb-0"
              labelClassName="fw-semibold"
              checked={formData.is_resolved}
              label={t(`${base}.fields.markAsResolved`)}
              name={VALIDATION_FIELD_MAPPING.markAsResolved}
              disabled={readOnly}
              onChange={onInputChange}
            />
            <span className="text-body small">— {t(`${base}.fields.adminOnly`)}</span>
          </div>
        )}
        <div className="col-12">
          <div className="mb-1 d-flex justify-content-between align-items-center">
            <div>
              <span className="fw-semibold">{t(`${base}.fields.baseCommissionOverride.label`)} ($)</span>
              <span className="text-body"> — {t(`${base}.fields.baseCommissionOverride.description`)}</span>
            </div>
            {isAdminCaller && !readOnly && (
              <Link
                text={t(`${base}.fields.baseCommissionOverride.resetToAuto`)}
                onClick={() => onResetToAuto('base_commission_override')}
                className="small"
              />
            )}
          </div>
          <Input
            type="number"
            id={VALIDATION_FIELD_MAPPING.baseCommissionOverride}
            name={VALIDATION_FIELD_MAPPING.baseCommissionOverride}
            label={t(`${base}.fields.baseCommissionOverride.label`)}
            labelClassName="visually-hidden"
            placeholder={t(`${base}.fields.baseCommissionOverride.placeholder`)}
            value={formData.base_commission_override}
            disabled={readOnly || !isAdminCaller}
            onChange={onInputChange}
          />
        </div>

        <div className="col-12">
          <div className="mb-1 d-flex justify-content-between align-items-center">
            <div>
              <span className="fw-semibold">{t(`${base}.fields.propertyVisitCommissionOverride.label`)} ($)</span>
              <span className="text-body"> — {t(`${base}.fields.propertyVisitCommissionOverride.description`)}</span>
            </div>
            {isAdminCaller && !readOnly && (
              <Link
                text={t(`${base}.fields.propertyVisitCommissionOverride.resetToAuto`)}
                onClick={() => onResetToAuto('property_visit_commission_override')}
                className="small"
              />
            )}
          </div>
          <Input
            type="number"
            id={VALIDATION_FIELD_MAPPING.propertyVisitCommissionOverride}
            name={VALIDATION_FIELD_MAPPING.propertyVisitCommissionOverride}
            label={t(`${base}.fields.propertyVisitCommissionOverride.label`)}
            labelClassName="visually-hidden"
            placeholder={t(`${base}.fields.propertyVisitCommissionOverride.placeholder`)}
            value={formData.property_visit_commission_override}
            disabled={readOnly || !isAdminCaller}
            onChange={onInputChange}
          />
        </div>

        <div className="col-12">
          <div className="mb-1">
            <span className="fw-semibold">{t(`${base}.fields.managerNote.label`)}</span>
            <span className="text-body"> — {t(`${base}.fields.managerNote.description`)}</span>
          </div>
          <TextArea
            id={VALIDATION_FIELD_MAPPING.managerNote}
            name={VALIDATION_FIELD_MAPPING.managerNote}
            label={t(`${base}.fields.managerNote.label`)}
            labelClassName="visually-hidden"
            placeholder={t(`${base}.fields.managerNote.placeholder`)}
            rows={3}
            value={formData.manager_note}
            disabled={readOnly}
            onChange={onInputChange}
          />
        </div>

        <div className="col-12">
          <div className="mb-1">
            <span className="fw-semibold">{t(`${base}.fields.adminNote.label`)}</span>
            <span className="text-body"> — {t(`${base}.fields.adminNote.description`)}</span>
          </div>
          <TextArea
            id={VALIDATION_FIELD_MAPPING.adminNote}
            name={VALIDATION_FIELD_MAPPING.adminNote}
            label={t(`${base}.fields.adminNote.label`)}
            labelClassName="visually-hidden"
            placeholder={t(`${base}.fields.adminNote.placeholder`)}
            rows={3}
            value={formData.admin_note}
            disabled={readOnly || !isAdminCaller}
            onChange={onInputChange}
          />
        </div>
      </div>
    </div>
  );
};

export default AdjustCommissionModalContent;
